import type { HoComponents } from '@pos/api-client';

/**
 * The menu being edited (FR-MENU-01..05): a layout's pages with their tiles, and the items and deals on sale. Nothing
 * reaches the server until it is saved, all at once.
 */
type EditorDto = HoComponents['schemas']['MenuEditorDto'];

export type Kind = 'item' | 'deal';
/** What the add dialog picks: items and deals, or item groups (ADR 0052), which become a group button and their variants. */
export type AddKind = Kind | 'group';
export const PALETTE = ['neutral', 'blue', 'cyan', 'teal', 'green', 'purple', 'magenta', 'red', 'warm-gray'] as const;

/** The unit an item is sold in on a tile or entry; `uomId` null is the item's default selling unit (ADR 0030). */
export interface TargetUnit {
  uomId: string | null;
  code: string;
  name: string;
}

export interface Target {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  /** For an item: the unit chosen (a menu holds an item once per unit). */
  unit?: TargetUnit;
  /** For an item group: its variants, in the group's order. */
  members?: GroupMember[];
}

export interface GroupMember {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

/** What identifies an item on a page or menu: the item and its unit (null: the default unit). */
export const unitKey = (targetId: string, uomId?: string | null) => `${targetId}|${uomId ?? ''}`;

/** What identifies an item group's button on a page, next to the `unitKey`s of items and deals. */
export const groupKey = (groupId: string) => `group|${groupId}`;

/** The variant a group button sells when variants aren't shown: the active default, else the first active one. */
export const fallbackMember = (members: readonly GroupMember[]) =>
  members.find((m) => m.isDefault && m.isActive) ?? members.find((m) => m.isActive);

export interface DraftTile {
  key: string;
  buttonId?: string;
  version?: number;
  targetType: 'item' | 'deal' | 'page';
  targetId: string;
  /** The unit of an item tile; null is the item's default selling unit. */
  uomId: string | null;
  uomCode: string | null;
  uomName: string | null;
  code: string;
  name: string;
  isActive: boolean;
  row: number;
  col: number;
  rowSpan: number;
  colSpan: number;
  label: string;
  paletteKey: string;
  /** Set when the tile is its item group's button (ADR 0052); the item is the fallback. */
  itemGroupId: string | null;
  /** The item group the tile's item is a variant of, so it can become that group's button. */
  memberOfGroupId: string | null;
  memberOfGroupName: string | null;
  removed: boolean;
  original?: string;
}

export interface DraftPage {
  key: string;
  pageId?: string;
  version?: number;
  name: string;
  paletteKey: string;
  sortOrder: number;
  removed: boolean;
  original?: string;
  /** Where it was when loaded, to send a move only when it moved. */
  placed?: string;
  tiles: DraftTile[];
  /** The page it is under (another page's key); null for a category tab (ADR 0138). */
  parentKey: string | null;
  /** A category tab's icon on the POS section rail (ADR 0139); null shows its colour dot. */
  icon: string | null;
  /** A sub-page that no tile on its parent opens yet (made before ADR 0138): the POS can't reach it. */
  linkMissing: boolean;
  /** Asks the server to add the missing tile on the same parent. */
  relink?: boolean;
  /** The removed page it moved up from, to go back under when that removal is undone. */
  upFrom?: string;
}

export interface DraftEntry {
  key: string;
  entryId?: string;
  version?: number;
  kind: Kind;
  targetId: string;
  uomId: string | null;
  uomCode: string | null;
  uomName: string | null;
  code: string;
  name: string;
  isActive: boolean;
  isVisible: boolean;
  sortOrder: number;
  removed: boolean;
  original?: string;
}

export interface MenuDraft {
  layoutId: string;
  pages: DraftPage[];
  entries: DraftEntry[];
}

export interface Grid {
  rows: number;
  cols: number;
}

let counter = 0;
const nextKey = (prefix: string) => `${prefix}-${++counter}`;

const tileState = (t: Pick<DraftTile, 'row' | 'col' | 'label' | 'paletteKey' | 'removed' | 'itemGroupId'>) =>
  JSON.stringify([t.row, t.col, t.label, t.paletteKey, t.removed, t.itemGroupId ?? null]);
const pageState = (p: Pick<DraftPage, 'name' | 'paletteKey' | 'sortOrder' | 'removed' | 'parentKey' | 'relink' | 'icon'>) =>
  JSON.stringify([p.name, p.paletteKey, p.sortOrder, p.removed, p.parentKey, p.relink ?? false, p.icon]);
const placeState = (p: Pick<DraftPage, 'parentKey' | 'relink'>) => JSON.stringify([p.parentKey, p.relink ?? false]);
const entryState = (e: Pick<DraftEntry, 'isVisible' | 'sortOrder' | 'removed'>) =>
  JSON.stringify([e.isVisible, e.sortOrder, e.removed]);

export function fromEditor(dto: EditorDto): MenuDraft {
  return {
    layoutId: dto.layoutId ?? '',
    pages: dto.pages.map((p) => {
      const page: DraftPage = {
        key: p.pageId,
        pageId: p.pageId,
        version: Number(p.version),
        name: p.name,
        paletteKey: p.paletteKey,
        sortOrder: Number(p.sortOrder),
        removed: false,
        parentKey: p.parentPageId ?? null,
        linkMissing: Boolean(p.linkMissing),
        icon: p.icon ?? null,
        tiles: p.tiles.map((t) => {
          const tile: DraftTile = {
            key: t.buttonId,
            buttonId: t.buttonId,
            version: Number(t.version),
            targetType: t.targetType as DraftTile['targetType'],
            targetId: t.targetId ?? '',
            uomId: t.uomId ?? null,
            uomCode: t.uomCode ?? null,
            uomName: t.uomName ?? null,
            code: t.code,
            name: t.name,
            isActive: t.isActive,
            row: Number(t.gridRow),
            col: Number(t.gridCol),
            rowSpan: Number(t.rowSpan),
            colSpan: Number(t.colSpan),
            label: t.labelOverride ?? '',
            paletteKey: t.paletteKey ?? '',
            itemGroupId: t.itemGroupId ?? null,
            memberOfGroupId: t.memberOfGroupId ?? null,
            memberOfGroupName: t.memberOfGroupName ?? null,
            removed: false,
          };
          return { ...tile, original: tileState(tile) };
        }),
      };
      return { ...page, original: pageState(page), placed: placeState(page) } as DraftPage;
    }),
    entries: dto.entries.map((e) => {
      const entry: DraftEntry = {
        key: e.entryId,
        entryId: e.entryId,
        version: Number(e.version),
        kind: e.kind as Kind,
        targetId: e.targetId,
        uomId: e.uomId ?? null,
        uomCode: e.uomCode ?? null,
        uomName: e.uomName ?? null,
        code: e.code,
        name: e.name,
        isActive: e.isActive,
        isVisible: e.isVisible,
        sortOrder: Number(e.sortOrder),
        removed: false,
      };
      return { ...entry, original: entryState(entry) };
    }),
  };
}

const pageChanged = (p: DraftPage) => !p.pageId || p.original !== pageState(p);
const tileChanged = (t: DraftTile) => !t.buttonId || t.original !== tileState(t);
const entryChanged = (e: DraftEntry) => !e.entryId || e.original !== entryState(e);

export const isNewOrChanged = { page: pageChanged, tile: tileChanged, entry: entryChanged };

/** How many things will be saved. */
export function changes(draft: MenuDraft) {
  let count = 0;
  for (const p of draft.pages) {
    if (p.removed) {
      if (p.pageId) count++;
      continue;
    }
    if (pageChanged(p)) count++;
    count += p.tiles.filter((t) => (t.removed ? Boolean(t.buttonId) : tileChanged(t))).length;
  }
  count += draft.entries.filter((e) => (e.removed ? Boolean(e.entryId) : entryChanged(e))).length;
  return count;
}

/** New pages, one per name, after the existing ones. */
export function addPages(draft: MenuDraft, names: readonly string[], paletteKey = 'neutral'): MenuDraft {
  let order = Math.max(0, ...draft.pages.map((p) => p.sortOrder));
  const pages = names
    .map((n) => n.trim())
    .filter(Boolean)
    .map((name) => ({
      key: nextKey('page'),
      name,
      paletteKey,
      sortOrder: (order += 1),
      removed: false,
      tiles: [],
      parentKey: null,
      linkMissing: false,
      icon: null,
    }));
  return { ...draft, pages: [...draft.pages, ...pages] };
}

/**
 * A new page under another (ADR 0138): it comes after the parent's other sub-pages, and the server puts a tile that opens it on
 * the parent when it is saved.
 */
export function addSubPage(draft: MenuDraft, parentKey: string, name: string): MenuDraft {
  const siblings = draft.pages.filter((p) => p.parentKey === parentKey);
  const page: DraftPage = {
    key: nextKey('page'),
    name: name.trim(),
    paletteKey: draft.pages.find((p) => p.key === parentKey)?.paletteKey ?? 'neutral',
    sortOrder: Math.max(0, ...siblings.map((p) => p.sortOrder)) + 1,
    removed: false,
    tiles: [],
    parentKey,
    linkMissing: false,
    icon: null,
  };
  return { ...draft, pages: [...draft.pages, page] };
}

/** The page and every page below it, by key. */
function branch(draft: MenuDraft, key: string) {
  const below = new Set([key]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const p of draft.pages)
      if (p.parentKey && below.has(p.parentKey) && !below.has(p.key)) {
        below.add(p.key);
        grew = true;
      }
  }
  return below;
}

/** The pages a page can be moved under: any other page of the layout but itself and the pages below it. */
export function parentChoices(draft: MenuDraft, key: string) {
  const mine = branch(draft, key);
  return pageTree(draft).filter(({ page }) => !page.removed && !mine.has(page.key));
}

/** Moves a page under another (its tile follows when saved), or to the top as a category tab (null). */
export function movePage(draft: MenuDraft, key: string, parentKey: string | null): MenuDraft {
  return updatePage(draft, key, { parentKey, relink: false, linkMissing: false });
}

/** Asks for the missing tile that opens a sub-page on its parent (ADR 0138). */
export function addMissingLink(draft: MenuDraft, key: string): MenuDraft {
  return updatePage(draft, key, { relink: true });
}

/** The pages in menu order: each category, then the pages under it, depth first, each level by its order. */
export function pageTree(draft: MenuDraft): { page: DraftPage; depth: number; parent?: DraftPage }[] {
  const byKey = new Map(draft.pages.map((p) => [p.key, p]));
  const childrenOf = new Map<string | null, DraftPage[]>();
  for (const page of draft.pages) {
    const parent = page.parentKey && byKey.has(page.parentKey) ? page.parentKey : null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), page]);
  }
  const out: { page: DraftPage; depth: number; parent?: DraftPage }[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const page of [...(childrenOf.get(parent) ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (seen.has(page.key)) continue;
      seen.add(page.key);
      out.push({ page, depth, parent: parent ? byKey.get(parent) : undefined });
      walk(page.key, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** The empty cells of a page, row by row. */
function freeCells(page: DraftPage, grid: Grid) {
  const taken = new Set<string>();
  for (const t of page.tiles.filter((x) => !x.removed))
    for (let r = t.row; r < t.row + t.rowSpan; r++)
      for (let c = t.col; c < t.col + t.colSpan; c++) taken.add(`${r},${c}`);
  const cells: [number, number][] = [];
  for (let r = 0; r < grid.rows; r++)
    for (let c = 0; c < grid.cols; c++) if (!taken.has(`${r},${c}`)) cells.push([r, c]);
  return cells;
}

/**
 * Puts items or deals on a page in its next free cells, and on sale on the menu. Those already on the page are left
 * out; what doesn't fit is reported, not placed.
 */
export function addTiles(
  draft: MenuDraft,
  pageKey: string,
  kind: Kind,
  targets: readonly Target[],
  grid: Grid,
): { draft: MenuDraft; placed: number; left: number } {
  let placed = 0;
  let left = 0;
  const pages = draft.pages.map((page) => {
    if (page.key !== pageKey) return page;
    const cells = freeCells(page, grid);
    const onPage = new Set(page.tiles.filter((t) => !t.removed).map((t) => unitKey(t.targetId, t.uomId)));
    const tiles = [...page.tiles];
    for (const target of targets) {
      if (onPage.has(unitKey(target.id, target.unit?.uomId))) continue;
      const cell = cells.shift();
      if (!cell) {
        left++;
        continue;
      }
      placed++;
      onPage.add(unitKey(target.id, target.unit?.uomId));
      tiles.push({
        key: nextKey('tile'),
        targetType: kind,
        targetId: target.id,
        uomId: target.unit?.uomId ?? null,
        uomCode: target.unit?.code ?? null,
        uomName: target.unit?.name ?? null,
        code: target.code,
        name: target.name,
        isActive: target.isActive,
        row: cell[0],
        col: cell[1],
        rowSpan: 1,
        colSpan: 1,
        label: '',
        paletteKey: '',
        // Known once saved: the editor then offers the group button for an item that is a variant.
        itemGroupId: null,
        memberOfGroupId: null,
        memberOfGroupName: null,
        removed: false,
      });
    }
    return { ...page, tiles };
  });
  const placedTargets = targets.filter((t) =>
    pages.some(
      (p) =>
        p.key === pageKey &&
        p.tiles.some(
          (x) => x.targetId === t.id && (x.uomId ?? null) === (t.unit?.uomId ?? null) && !x.buttonId && !x.removed,
        ),
    ),
  );
  return { draft: addEntries({ ...draft, pages }, kind, placedTargets), placed, left };
}

/**
 * Puts item groups on a page, one group button each in the next free cell (ADR 0052): the button opens the group's
 * variants on the POS, and sells its default variant where variants aren't shown. Every active variant goes on sale on
 * the menu, so the POS and the online menus can offer it. A group already on the page, or without an active variant,
 * is left out; what doesn't fit is reported, not placed.
 */
export function addGroupTiles(
  draft: MenuDraft,
  pageKey: string,
  groups: readonly Target[],
  grid: Grid,
): { draft: MenuDraft; placed: number; left: number } {
  let placed = 0;
  let left = 0;
  const added: Target[] = [];
  const pages = draft.pages.map((page) => {
    if (page.key !== pageKey) return page;
    const cells = freeCells(page, grid);
    const onPage = new Set(page.tiles.filter((t) => !t.removed && t.itemGroupId).map((t) => t.itemGroupId));
    const tiles = [...page.tiles];
    for (const group of groups) {
      const fallback = fallbackMember(group.members ?? []);
      if (!fallback || onPage.has(group.id)) continue;
      const cell = cells.shift();
      if (!cell) {
        left++;
        continue;
      }
      placed++;
      onPage.add(group.id);
      added.push(group);
      tiles.push({
        key: nextKey('tile'),
        targetType: 'item',
        targetId: fallback.id,
        uomId: null,
        uomCode: null,
        uomName: null,
        code: fallback.code,
        name: fallback.name,
        isActive: fallback.isActive,
        row: cell[0],
        col: cell[1],
        rowSpan: 1,
        colSpan: 1,
        label: '',
        paletteKey: '',
        itemGroupId: group.id,
        memberOfGroupId: group.id,
        memberOfGroupName: group.name,
        removed: false,
      });
    }
    return { ...page, tiles };
  });
  return { draft: addGroupEntries({ ...draft, pages }, added), placed, left };
}

/** Puts item groups' active variants on sale on the menu, without a tile: the online menus show them as one product. */
export function addGroupEntries(draft: MenuDraft, groups: readonly Target[]): MenuDraft {
  const onMenu = new Set(draft.entries.filter((e) => !e.removed && e.kind === 'item').map((e) => e.targetId));
  const variants = groups
    .flatMap((g) => g.members ?? [])
    .filter((m) => m.isActive && !onMenu.has(m.id))
    .map((m) => ({ id: m.id, code: m.code, name: m.name, isActive: m.isActive }));
  return addEntries(draft, 'item', variants);
}

/** Puts items or deals on sale on the menu (found by search), without a tile. */
export function addEntries(draft: MenuDraft, kind: Kind, targets: readonly Target[]): MenuDraft {
  const entries = [...draft.entries];
  let order = Math.max(0, ...entries.map((e) => e.sortOrder));
  for (const target of targets) {
    const uomId = target.unit?.uomId ?? null;
    const existing = entries.findIndex((e) => e.targetId === target.id && (e.uomId ?? null) === uomId);
    const found = entries[existing];
    if (found) {
      if (found.removed) entries[existing] = { ...found, removed: false, isVisible: true };
      continue;
    }
    entries.push({
      key: nextKey('entry'),
      kind,
      targetId: target.id,
      uomId,
      uomCode: target.unit?.code ?? null,
      uomName: target.unit?.name ?? null,
      code: target.code,
      name: target.name,
      isActive: target.isActive,
      isVisible: true,
      sortOrder: (order += 10),
      removed: false,
    });
  }
  return { ...draft, entries };
}

export function updatePage(
  draft: MenuDraft,
  pageKey: string,
  change: Partial<Pick<DraftPage, 'name' | 'paletteKey' | 'sortOrder' | 'removed' | 'parentKey' | 'relink' | 'linkMissing' | 'icon'>>,
): MenuDraft {
  const page = draft.pages.find((p) => p.key === pageKey);
  let pages = draft.pages;
  if (page && change.removed === true) {
    // Its sub-pages move up to its parent, as the server does (ADR 0138); undoing the removal puts them back.
    pages = pages.map((p) => (p.parentKey === pageKey ? { ...p, parentKey: page.parentKey, upFrom: pageKey } : p));
    if (!page.pageId) return { ...draft, pages: pages.filter((p) => p.key !== pageKey) };
  }
  if (page && change.removed === false)
    pages = pages.map((p) => (p.upFrom === pageKey ? { ...p, parentKey: pageKey, upFrom: undefined } : p));
  return { ...draft, pages: pages.map((p) => (p.key === pageKey ? { ...p, ...change } : p)) };
}

export function updateTile(
  draft: MenuDraft,
  pageKey: string,
  tileKey: string,
  change: Partial<Pick<DraftTile, 'row' | 'col' | 'label' | 'paletteKey' | 'removed' | 'itemGroupId'>>,
): MenuDraft {
  return {
    ...draft,
    pages: draft.pages.map((p) =>
      p.key !== pageKey
        ? p
        : {
            ...p,
            tiles:
              change.removed && p.tiles.find((t) => t.key === tileKey && !t.buttonId)
                ? p.tiles.filter((t) => t.key !== tileKey)
                : p.tiles.map((t) => (t.key === tileKey ? { ...t, ...change } : t)),
          },
    ),
  };
}

export function updateEntry(
  draft: MenuDraft,
  entryKey: string,
  change: Partial<Pick<DraftEntry, 'isVisible' | 'sortOrder' | 'removed'>>,
): MenuDraft {
  if (change.removed && draft.entries.find((e) => e.key === entryKey && !e.entryId))
    return { ...draft, entries: draft.entries.filter((e) => e.key !== entryKey) };
  return { ...draft, entries: draft.entries.map((e) => (e.key === entryKey ? { ...e, ...change } : e)) };
}

/** Items and deals on sale that no page shows: found by search only. */
export function offPage(draft: MenuDraft) {
  const tiled = new Set(
    draft.pages
      .filter((p) => !p.removed)
      .flatMap((p) => p.tiles.filter((t) => !t.removed).map((t) => unitKey(t.targetId, t.uomId))),
  );
  return draft.entries.filter((e) => !tiled.has(unitKey(e.targetId, e.uomId)));
}

export type Problem = 'name' | 'outside' | 'overlap';

/** What stops saving, by page or tile key. */
export function problems(draft: MenuDraft, grid: Grid): Map<string, Problem> {
  const found = new Map<string, Problem>();
  for (const page of draft.pages.filter((p) => !p.removed)) {
    if (!page.name.trim()) found.set(page.key, 'name');
    const cells = new Map<string, string>();
    for (const tile of page.tiles.filter((t) => !t.removed)) {
      if (tile.row < 0 || tile.col < 0 || tile.row + tile.rowSpan > grid.rows || tile.col + tile.colSpan > grid.cols) {
        found.set(tile.key, 'outside');
        continue;
      }
      for (let r = tile.row; r < tile.row + tile.rowSpan; r++)
        for (let c = tile.col; c < tile.col + tile.colSpan; c++) {
          const other = cells.get(`${r},${c}`);
          if (other) {
            found.set(tile.key, 'overlap');
            found.set(other, 'overlap');
          } else cells.set(`${r},${c}`, tile.key);
        }
    }
  }
  return found;
}

export function toRequest(draft: MenuDraft) {
  return {
    layoutId: draft.layoutId,
    pages: draft.pages
      .filter((p) =>
        p.removed
          ? Boolean(p.pageId)
          : pageChanged(p) || p.tiles.some((t) => (t.removed ? Boolean(t.buttonId) : tileChanged(t))),
      )
      .map((p) => ({
        pageId: p.pageId ?? null,
        version: p.version ?? null,
        // A new page is named by its key, so another new page can be under it; a move sends where it goes ('' for the top).
        key: p.pageId ? null : p.key,
        parent:
          !p.pageId || p.placed !== placeState(p) ? (p.parentKey ?? (p.pageId ? '' : null)) : null,
        name: p.name.trim(),
        // Sent only when it changed: '' clears it.
        icon: !p.pageId || pageChanged(p) ? (p.icon ?? '') : null,
        paletteKey: p.paletteKey,
        sortOrder: p.sortOrder,
        removed: p.removed,
        tiles: p.removed
          ? []
          : p.tiles
              .filter((t) => (t.removed ? Boolean(t.buttonId) : tileChanged(t)))
              .map((t) => ({
                buttonId: t.buttonId ?? null,
                version: t.version ?? null,
                targetType: t.targetType,
                targetId: t.targetId,
                uomId: t.targetType === 'item' ? t.uomId : null,
                gridRow: t.row,
                gridCol: t.col,
                rowSpan: t.rowSpan,
                colSpan: t.colSpan,
                labelOverride: t.label.trim() || null,
                paletteKey: t.paletteKey || null,
                itemGroupId: t.targetType === 'item' ? t.itemGroupId : null,
                removed: t.removed,
              })),
      })),
    entries: draft.entries
      .filter((e) => (e.removed ? Boolean(e.entryId) : entryChanged(e)))
      .map((e) => ({
        entryId: e.entryId ?? null,
        version: e.version ?? null,
        kind: e.kind,
        targetId: e.targetId,
        uomId: e.kind === 'item' ? e.uomId : null,
        isVisible: e.isVisible,
        sortOrder: e.sortOrder,
        removed: e.removed,
      })),
  };
}
